import { Link, useSearchParams } from "react-router-dom";
import { specials } from "@/mocks/specials";
import { movies } from "@/mocks/movies";
import MovieCard from "@/components/feature/MovieCard";
import useDocumentTitle from "@/hooks/useDocumentTitle";

export default function Special() {
  const [searchParams, setSearchParams] = useSearchParams();
  const id = searchParams.get("id");
  const current = specials.find((s) => s.id === id) || specials[0];

  useDocumentTitle(current ? `${current.title} - 诱人的飞行` : "影视专题 - 诱人的飞行");

  if (!current) {
    return (
      <div className="w-full px-4 md:px-8 py-24 text-center">
        <i className="ri-gallery-line text-5xl text-foreground-400 mb-5 inline-block" aria-hidden="true"></i>
        <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">暂无专题内容</h1>
        <p className="text-sm text-foreground-600">专题正在策划中，敬请期待。</p>
      </div>
    );
  }

  const collection = current.movieIds
    .map((mid) => movies.find((m) => m.id === mid))
    .filter((m): m is (typeof movies)[number] => Boolean(m));
  const others = specials.filter((s) => s.id !== current.id);

  return (
    <div className="w-full">
      {/* 专题主图 */}
      <div className="relative w-full h-[340px] md:h-[460px] overflow-hidden bg-primary-950">
        <img
          src={current.cover}
          alt={`${current.title} 专题主图`}
          title={`${current.title} 影视专题`}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/30"></div>
        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 md:px-8 pb-8 md:pb-12 animate-fade-up">
            <nav className="text-xs text-background-300 mb-3 flex items-center gap-1.5">
              <Link to="/" className="hover:text-accent-400 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
              <span className="text-background-100">影视专题</span>
            </nav>
            <span className="inline-block px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-bold mb-3">专题策划</span>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-2">{current.title}</h1>
            <p className="text-sm md:text-base text-background-200 max-w-2xl">{current.subtitle}</p>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        {/* 导语 */}
        <section className="max-w-3xl mb-10">
          <p className="text-sm md:text-base text-foreground-700 leading-loose">{current.intro}</p>
        </section>

        {/* 专题切换 */}
        <div className="mb-8">
          <h2 className="text-sm font-medium text-foreground-500 mb-3">更多专题</h2>
          <div className="flex flex-wrap gap-2.5">
            {specials.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSearchParams({ id: s.id })}
                className={`px-4 py-2 rounded-md text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  s.id === current.id
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 border border-background-200 text-foreground-700 hover:border-primary-300"
                }`}
              >
                {s.title}
              </button>
            ))}
          </div>
        </div>

        {/* 合集 */}
        <section className="mb-10">
          <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5 flex items-center gap-2">
            <span className="w-1 h-5 rounded bg-accent-500"></span>
            专题片单
          </h2>
          {collection.length === 0 ? (
            <p className="text-sm text-foreground-500 py-10 text-center border border-dashed border-background-300 rounded-lg">该专题内容整理中</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-5">
              {collection.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          )}
        </section>

        {/* 推荐理由 */}
        <section className="p-6 md:p-8 rounded-lg bg-secondary-100 border border-secondary-200 mb-10">
          <h2 className="text-lg font-heading font-bold text-foreground-950 mb-3 flex items-center gap-2">
            <i className="ri-quill-pen-line text-accent-500" aria-hidden="true"></i>
            编辑推荐理由
          </h2>
          <p className="text-sm md:text-base text-foreground-700 leading-loose">{current.reason}</p>
          <p className="text-xs text-foreground-500 mt-4">整理于 {current.date}</p>
        </section>

        {/* 相关专题 */}
        {others.length > 0 && (
          <section>
            <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-5 flex items-center gap-2">
              <span className="w-1 h-5 rounded bg-accent-500"></span>
              相关专题
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              {others.map((s) => (
                <Link
                  key={s.id}
                  to={`/special?id=${s.id}`}
                  className="group relative h-[180px] rounded-lg overflow-hidden border border-background-200 cursor-pointer"
                >
                  <img src={s.cover} alt={`${s.title} 专题`} title={`${s.title} 影视专题`} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 to-primary-950/20"></div>
                  <div className="absolute inset-0 p-5 flex flex-col justify-end">
                    <h3 className="text-base md:text-lg font-heading font-bold text-background-50 mb-1">{s.title}</h3>
                    <p className="text-xs text-background-300 line-clamp-1">{s.subtitle}</p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}